import * as z from "zod";
import { api } from "@/lib/http";

export type Company = {
  id: string;
  company_name: string;
  description: string;
  company_logo: string;
  created_at: string;
  updated_at: string;
};

export type TemplatePolicies = Record<
  string,
  Array<{
    entity: string;
    priority_index: number;
  }>
>;

// Zod schemas for validation
export const createCompanySchema = z.object({
  company_name: z.string().min(1, "Company name is required"),
  description: z.string().min(1, "Description is required"),
  company_logo: z.file().optional(),
  users_file: z.file().optional(),
  policies: z.array(z.string()).optional(),
});

export const updateCompanySchema = z.object({
  company_name: z.string().min(1, "Company name is required"),
  description: z.string().min(1, "Description is required"),
  company_logo: z.file().optional(),
});

// Inferred types from schemas
export type CreateCompanyPayload = z.infer<typeof createCompanySchema>;
export type UpdateCompanyPayload = z.infer<typeof updateCompanySchema>;

async function getCompany() {
  return api.auth.raw.get("api/v1/company/").json<Company>();
}

async function getTemplatePolicies() {
  return api.auth.raw
    .get("api/v1/policies/template-policies")
    .json<TemplatePolicies>();
}

async function createCompany(payload: CreateCompanyPayload) {
  const formData = new FormData();
  formData.append("company_name", payload.company_name);
  formData.append("description", payload.description);

  if (payload.company_logo) {
    formData.append("company_logo", payload.company_logo);
  }

  if (payload.users_file) {
    formData.append("users_file", payload.users_file);
  }

  if (payload.policies) {
    payload.policies.forEach((policy) => {
      formData.append("policies", policy);
    });
  }

  return api.auth.raw.post("api/v1/company/", { body: formData }).json<Company>();
}

async function updateCompany(payload: UpdateCompanyPayload) {
  const formData = new FormData();

  if (payload.company_name) {
    formData.append("company_name", payload.company_name);
  }

  if (payload.description) {
    formData.append("description", payload.description);
  }

  if (payload.company_logo) {
    formData.append("company_logo", payload.company_logo);
  }

  return api.auth.raw.patch("api/v1/company/", { body: formData }).json<Company>();
}

const company = {
  getCompany,
  getTemplatePolicies,
  createCompany,
  updateCompany,
};

export default company;
